import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, Truck, FileText } from "lucide-react";
import api, { formatApiError, imgUrl } from "../lib/api";
import SEO from "../components/SEO";
import { ReturnDialog, ModDialog } from "../components/OrderRequestDialogs";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const CANCEL_REASONS = ["Ordered by mistake", "Found a better price elsewhere", "Delivery time is too long", "Want to change my order", "Change of mind", "Other"];

const STEPS = ["Placed", "Confirmed", "Shipped", "Delivered"];
const API_BASE = `/api`;

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [courier, setCourier] = useState(null);
  const [courierLoading, setCourierLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [modOpen, setModOpen] = useState(false);

  const confirmCancel = async () => {
    if (!cancelReason) return toast.error("Please select a cancellation reason");
    setCancelling(true);
    try {
      await api.post(`/orders/${order.order_number}/cancel`, { reason: cancelReason, email });
      toast.success("Order cancelled");
      setCancelOpen(false);
      const { data } = await api.get(`/orders/track?order_number=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`);
      setOrder(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCancelling(false);
    }
  };

  const checkCourier = async () => {
    setCourierLoading(true);
    try {
      const { data } = await api.get(`/orders/track-courier?order_number=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`);
      setCourier(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCourierLoading(false);
    }
  };

  const track = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track?order_number=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`);
      setOrder(data);
    } catch (err) {
      setOrder(null);
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("order") && email) track();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentStep = order ? (order.status === "Cancelled" ? -1 : STEPS.indexOf(order.status)) : -2;

  return (
    <div className="max-w-xl mx-auto px-4 py-16" data-testid="track-order-page">
      <SEO title="Track Order" description="Track your Humaira Crafts order status" />
      <h1 className="text-4xl tracking-tighter font-semibold mb-4">Track Your Order</h1>
      <p className="text-sm text-muted-foreground mb-10">Enter your order number and the email used at checkout.</p>

      <form onSubmit={track} className="flex flex-col gap-4">
        <Input required placeholder="Order Number (e.g. HC-100001)" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} className="bg-white" data-testid="track-order-number" />
        <Input required type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white" data-testid="track-email" />
        <button type="submit" disabled={loading} className="bg-[#111] text-[#FAF9F6] py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="track-submit">
          {loading ? "Tracking..." : "Track Order"}
        </button>
      </form>

      {order && (
        <div className="mt-12 bg-white border border-black/10 p-8" data-testid="tracking-result">
          <div className="flex items-center gap-3 mb-8">
            <Package strokeWidth={1.5} size={20} className="text-gold" />
            <div>
              <p className="font-serif text-xl">{order.order_number}</p>
              <p className="text-xs text-muted-foreground">Placed {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
          </div>

          {order.status === "Cancelled" ? (
            <p className="text-destructive font-medium" data-testid="order-cancelled">This order has been cancelled.</p>
          ) : (
            <div className="flex flex-col gap-0">
              {STEPS.map((step, i) => {
                const done = i <= currentStep;
                const entry = order.status_history?.find((h) => h.status === step);
                return (
                  <div key={step} className="flex gap-4" data-testid={`track-step-${step.toLowerCase()}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full border-2 ${done ? "bg-gold border-gold" : "border-black/20"}`} />
                      {i < STEPS.length - 1 && <div className={`w-px h-10 ${i < currentStep ? "bg-gold" : "bg-black/10"}`} />}
                    </div>
                    <div className="pb-8">
                      <p className={`text-sm ${done ? "font-medium" : "text-muted-foreground"}`}>{step}</p>
                      {entry && <p className="text-xs text-muted-foreground">{new Date(entry.at).toLocaleString("en-IN")}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t border-black/10 pt-6 mt-2">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3 py-1.5">
                {i.image && <img src={imgUrl(i.image)} alt={i.name} className="w-10 h-10 object-cover border border-black/10" />}
                <span className="flex-1 text-sm text-muted-foreground">{i.name} × {i.qty}</span>
                <span className="text-sm">₹{(i.price * i.qty).toLocaleString("en-IN")}</span>
              </div>
            ))}
            <div className="border-t border-black/10 mt-2 pt-3 flex flex-col gap-1 text-sm">
              {order.subtotal != null && order.discount > 0 && (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>₹{order.subtotal.toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between text-green-800"><span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</span><span>−₹{order.discount.toLocaleString("en-IN")}</span></div>
                </>
              )}
              <div className="flex justify-between font-semibold text-base"><span>Total</span><span>₹{order.total.toLocaleString("en-IN")}</span></div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {order.payment_method === "cod" ? "Cash on Delivery" : "Paid Online"}{order.payment_status && order.payment_status !== "cod" ? ` · ${order.payment_status}` : ""}
            </p>
            {order.address && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="track-address">
                Deliver to: {order.customer?.name}, {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city}, {order.address.state} — {order.address.pincode}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-4">
              <a href={`${API_BASE}/orders/${order.order_number}/invoice?email=${encodeURIComponent(email)}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300"
                data-testid="download-invoice">
                <FileText strokeWidth={1.5} size={14} /> GST Invoice
              </a>
              {order.awb_code && (
                <button onClick={checkCourier} disabled={courierLoading}
                  className="flex items-center gap-2 border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300"
                  data-testid="check-courier">
                  <Truck strokeWidth={1.5} size={14} /> {courierLoading ? "Checking..." : "Courier Status"}
                </button>
              )}
            </div>
            {order.awb_code && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="courier-info">
                Shipped via {order.courier_name || "courier"} · AWB {order.awb_code}
                {courier && <span className="text-gold"> · {courier.status}</span>}
              </p>
            )}
            {["Placed", "Confirmed", "Processing"].includes(order.status) && (
              <button onClick={() => setModOpen(true)} className="mt-4 mr-5 text-xs uppercase tracking-widest border-b border-black hover:border-gold hover:text-gold transition-colors duration-300" data-testid="track-request-change">
                Request Order Change
              </button>
            )}
            {order.status === "Delivered" && (
              <button onClick={() => setReturnOpen(true)} className="mt-4 mr-5 text-xs uppercase tracking-widest border-b border-black hover:border-gold hover:text-gold transition-colors duration-300" data-testid="track-request-return">
                Return / Refund / Exchange
              </button>
            )}
            {["Placed", "Confirmed"].includes(order.status) && (
              <button onClick={() => setCancelOpen(true)} className="mt-4 text-xs uppercase tracking-widest text-destructive border-b border-destructive hover:opacity-70 transition-opacity duration-300" data-testid="track-cancel-order">
                Cancel This Order
              </button>
            )}
            {order.status === "Cancelled" && order.cancellation_reason && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="track-cancel-reason">
                Cancellation reason: {order.cancellation_reason}{order.refund_status === "pending" ? " · Refund pending" : ""}
              </p>
            )}
          </div>
        </div>
      )}

      {order && returnOpen && (
        <ReturnDialog order={order} email={email} onClose={() => setReturnOpen(false)} onDone={() => setReturnOpen(false)} />
      )}
      {order && modOpen && (
        <ModDialog order={order} email={email} onClose={() => setModOpen(false)} onDone={() => setModOpen(false)} />
      )}

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-[#FAF9F6] max-w-md" data-testid="track-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cancel Order {order?.order_number}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">This cannot be undone. Stock will be released and any online payment will be refunded.</p>
          <Select value={cancelReason} onValueChange={setCancelReason}>
            <SelectTrigger className="rounded-sm bg-white" data-testid="track-cancel-reason-select"><SelectValue placeholder="Select a reason *" /></SelectTrigger>
            <SelectContent>
              {CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-3 mt-2">
            <button onClick={confirmCancel} disabled={cancelling} className="flex-1 bg-destructive text-white py-3 small-caps hover:opacity-90 transition-opacity duration-300 disabled:opacity-50" data-testid="track-confirm-cancel">
              {cancelling ? "Cancelling..." : "Confirm Cancellation"}
            </button>
            <button onClick={() => setCancelOpen(false)} className="flex-1 border border-black/20 py-3 small-caps hover:border-gold transition-colors duration-300" data-testid="track-keep-order">
              Keep Order
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
